import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, LogOut, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useAuthStore from '@/stores/authStore';
import { SETTINGS_GROUPS, type SettingsSectionDef } from './registry';
import './settings.css';

interface SettingsShellProps {
  sections: SettingsSectionDef[];
  activeId: string;
  onSelect: (id: string) => void;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Vollflächige Einstellungs-Hülle: ein Topbar-Streifen, eine Navigationsspalte,
 * eine Inhaltsspalte. Bewusst genau EINE Navigationsebene — die alte Hülle
 * zeigte dieselbe Auswahl gleichzeitig als Icon-Leiste, Liste und Pill-Reihe.
 */
export default function SettingsShell({
  sections,
  activeId,
  onSelect,
  onClose,
  children,
}: SettingsShellProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  // Die Kopfleiste der App ist auf /settings ausgeblendet — ohne diesen
  // Knopf gäbe es in den Einstellungen keine Möglichkeit, sich abzumelden.
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    }
  };

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase()
    || user?.email?.[0]?.toUpperCase()
    || '?';

  const label = (section: SettingsSectionDef) => t(section.titleKey, section.titleFallback);
  const describe = (section: SettingsSectionDef) => t(section.descKey, section.descFallback);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return sections;
    return sections.filter((section) =>
      `${label(section)} ${describe(section)}`.toLocaleLowerCase().includes(needle),
    );
  }, [sections, query, t]);

  const groups = useMemo(
    () =>
      SETTINGS_GROUPS.map((group) => ({
        ...group,
        items: filtered.filter((section) => section.group === group.id),
      })).filter((group) => group.items.length > 0),
    [filtered],
  );

  // Beim Wechsel des Abschnitts wieder nach oben — sonst startet der neue
  // Abschnitt auf der Scrollposition des vorherigen.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [activeId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);

      if (event.key === 'Escape' && !typing) {
        onClose();
        return;
      }
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="st-shell">
      <header className="st-topbar">
        <button
          type="button"
          className="st-iconbtn"
          onClick={onClose}
          aria-label={t('settings:nav.back', 'Zurück zum Desk')}
          title={t('settings:nav.back', 'Zurück zum Desk')}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="st-topbar__title">{t('shell:action.settings', 'Einstellungen')}</h1>
        <span className="st-topbar__spacer" />
        <span className="st-topbar__hint">Esc</span>
        <button
          type="button"
          className="st-iconbtn"
          onClick={onClose}
          aria-label={t('settings:nav.close', 'Einstellungen schließen')}
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="st-body">
        <nav className="st-nav" aria-label={t('shell:action.settings', 'Einstellungen')}>
          <div className="st-nav__search">
            <div className="st-nav__search-field">
              <Search />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('settings:nav.search', 'Suchen…')}
                aria-label={t('settings:nav.search', 'Suchen…')}
              />
              {query && (
                <button
                  type="button"
                  className="st-nav__search-clear"
                  onClick={() => {
                    setQuery('');
                    searchRef.current?.focus();
                  }}
                  aria-label={t('common:action.clear', 'Zurücksetzen')}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          <div className="st-nav__scroll sidebar-scrollbar">
            {groups.map((group) => (
              <div key={group.id} className="st-nav__group">
                <p className="st-nav__group-label">{t(group.labelKey, group.labelFallback)}</p>
                {group.items.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      className="st-nav__item"
                      data-active={section.id === activeId}
                      aria-current={section.id === activeId ? 'page' : undefined}
                      onClick={() => onSelect(section.id)}
                      title={describe(section)}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span>{label(section)}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            {groups.length === 0 && (
              <p className="st-nav__empty">
                {t('settings:nav.noResults', 'Keine Einstellung gefunden')}
              </p>
            )}
          </div>

          <div className="st-nav__footer">
            <span className="st-nav__avatar" aria-hidden="true">{initials}</span>
            <span className="st-nav__user">
              <strong>{[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email}</strong>
              <small>{user?.email}</small>
            </span>
            <button
              type="button"
              className="st-iconbtn st-nav__logout"
              onClick={handleLogout}
              aria-label={t('views:u.uLogout', 'Abmelden')}
              title={t('views:u.uLogout', 'Abmelden')}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </nav>

        <main className="st-main" ref={mainRef}>
          {children}
        </main>
      </div>
    </div>
  );
}
